import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { ShieldCheck } from 'lucide-react';
import { HierarchicalTeamPicker } from '@/components/common/hierarchical-team-picker';
import { useUsers } from '@/hooks/queries/use-users';

export type ScopeMode = 'my' | 'all' | 'team' | 'user';

interface ScopeSelectorProps {
  /**
   * The current scope mode selected
   */
  scope: ScopeMode;
  
  /**
   * The ID of the selected team or user (when scope is 'team' or 'user')
   */
  scopeId?: string;
  
  /**
   * Callback when the scope or scopeId changes
   */
  onScopeChange: (scope: ScopeMode, scopeId?: string) => void;
  
  /**
   * Label for the entity being scoped (e.g., 'Activity', 'Epics')
   * Used to customize the dropdown labels
   */
  entityLabel: string;

  /**
   * Whether the current user is a global admin.
   * When true and scope is 'all', shows an indicator that personal items are included.
   */
  isAdmin?: boolean;
}

const getScopeOptions = (entityLabel: string): Array<{ label: string; value: ScopeMode }> => [
  { label: `My ${entityLabel}`, value: 'my' },
  { label: `All ${entityLabel}`, value: 'all' },
  { label: 'By Team', value: 'team' },
  { label: 'By User', value: 'user' },
];

export function ScopeSelector({ 
  scope, 
  scopeId, 
  onScopeChange, 
  entityLabel,
  isAdmin,
}: ScopeSelectorProps) {
  const { data: usersData } = useUsers({ limit: 1000 });

  const users = useMemo(() => {
    const raw = usersData ?? [];
    return [...raw].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [usersData]);

  const scopeOptions = getScopeOptions(entityLabel);

  const handleScopeChange = (newScope: ScopeMode) => {
    if (newScope === 'my' || newScope === 'all') {
      onScopeChange(newScope, undefined);
    } else if (newScope === 'team') {
      // Preserve previous team selection if switching back to team scope
      onScopeChange(newScope, scope === 'team' ? scopeId : undefined);
    } else if (newScope === 'user') {
      // Auto-select first user so data loads immediately
      const firstUser = users[0];
      onScopeChange(newScope, firstUser?.id);
    } else {
      onScopeChange(newScope, undefined);
    }
  };

  const handleScopeIdChange = (newScopeId: string | null) => {
    onScopeChange(scope, newScopeId ?? undefined);
  };

  const showTeamSelector = scope === 'team';
  const showUserSelector = scope === 'user';

  return (
    <div className="flex items-center gap-2">
      <Select value={scope} onValueChange={(v) => handleScopeChange(v as ScopeMode)}>
        <SelectTrigger className="w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {scopeOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isAdmin && scope === 'all' && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground bg-white/[0.04] border border-white/[0.06] rounded-full px-2 py-0.5 cursor-default"
              aria-label={`Admin scope indicator. Includes all users' personal ${entityLabel.toLowerCase()}`}
            >
              <ShieldCheck className="h-3 w-3" aria-hidden="true" />
              <span className="hidden sm:inline" aria-hidden="true">Admin</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Includes all users&apos; personal {entityLabel.toLowerCase()}</p>
          </TooltipContent>
        </Tooltip>
      )}

      {showTeamSelector && (
        <HierarchicalTeamPicker
          value={scopeId ?? null}
          onChange={handleScopeIdChange}
          className="w-[260px]"
        />
      )}

      {showUserSelector && (
        <Select value={scopeId || ''} onValueChange={handleScopeIdChange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Select user..." />
          </SelectTrigger>
          <SelectContent>
            {users.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">No users available</div>
            ) : (
              users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
